// The scale formulas: every scale unit of the library as its degrees above the
// tonic, in ascending order. A degree is the scale step's number (1-7) with the
// accidental that moves it off the major scale's step: 'b3' is the minor third,
// '#4' the augmented fourth, 'bb7' the diminished seventh. The number fixes the
// note's letter (one letter per step, counted up from the tonic's letter) and
// the accidental fixes its pitch, so a formula is both the scale's sound and its
// spelling in any key (see scaleSpelling.ts).
//
// Tonic-agnostic and complete: all 86 units of the catalog in
// scaleLibraryCatalog.ts, in its family order, including the six Bebop scales,
// which the library lists in the catalog but does not ship as playable content
// yet (the generator mints nothing for them; see scaleCatalogContent.ts). Every
// formula spans one octave: its last step returns to the tonic.

export type ScaleDegree = string;

/** Unit id (scaleLibraryCatalog.ts) -> the unit's degrees, ascending from the tonic. */
export const SCALE_FORMULAS: Record<string, readonly ScaleDegree[]> = {
  ionian: ['1', '2', '3', '4', '5', '6', '7'],
  dorian: ['1', '2', 'b3', '4', '5', '6', 'b7'],
  phrygian: ['1', 'b2', 'b3', '4', '5', 'b6', 'b7'],
  lydian: ['1', '2', '3', '#4', '5', '6', '7'],
  mixolydian: ['1', '2', '3', '4', '5', '6', 'b7'],
  aeolian: ['1', '2', 'b3', '4', '5', 'b6', 'b7'],
  locrian: ['1', 'b2', 'b3', '4', 'b5', 'b6', 'b7'],
  'ascending-melodic-minor': ['1', '2', 'b3', '4', '5', '6', '7'],
  'phrygian-nat6': ['1', 'b2', 'b3', '4', '5', '6', 'b7'],
  'lydian-augmented': ['1', '2', '3', '#4', '#5', '6', '7'],
  'lydian-dominant': ['1', '2', '3', '#4', '5', '6', 'b7'],
  'aeolian-dominant': ['1', '2', '3', '4', '5', 'b6', 'b7'],
  'locrian-nat2': ['1', '2', 'b3', '4', 'b5', 'b6', 'b7'],
  superlocrian: ['1', 'b2', 'b3', 'b4', 'b5', 'b6', 'b7'],
  'harmonic-minor': ['1', '2', 'b3', '4', '5', 'b6', '7'],
  'locrian-nat6': ['1', 'b2', 'b3', '4', 'b5', '6', 'b7'],
  'ionian-augmented': ['1', '2', '3', '4', '#5', '6', '7'],
  romanian: ['1', '2', 'b3', '#4', '5', '6', 'b7'],
  'phrygian-dominant': ['1', 'b2', '3', '4', '5', 'b6', 'b7'],
  'lydian-2': ['1', '#2', '3', '#4', '5', '6', '7'],
  ultralocrian: ['1', 'b2', 'b3', 'b4', 'b5', 'b6', 'bb7'],
  'harmonic-major': ['1', '2', '3', '4', '5', 'b6', '7'],
  'dorian-b5': ['1', '2', 'b3', '4', 'b5', '6', 'b7'],
  'phrygian-b4': ['1', 'b2', 'b3', 'b4', '5', 'b6', 'b7'],
  'lydian-b3': ['1', '2', 'b3', '#4', '5', '6', '7'],
  'mixolydian-b2': ['1', 'b2', '3', '4', '5', '6', 'b7'],
  'lydian-augmented-2': ['1', '#2', '3', '#4', '#5', '6', '7'],
  'locrian-bb7': ['1', 'b2', 'b3', '4', 'b5', 'b6', 'bb7'],
  'fully-diminished': ['1', '2', 'b3', '4', 'b5', 'b6', 'bb7', '7'],
  'dominant-diminished': ['1', 'b2', 'b3', '3', '#4', '5', '6', 'b7'],
  'minor-pentatonic': ['1', 'b3', '4', '5', 'b7'],
  'major-pentatonic': ['1', '2', '3', '5', '6'],
  'egyptian-suspended': ['1', '2', '4', '5', 'b7'],
  'blues-minor-pentatonic': ['1', 'b3', '4', 'b6', 'b7'],
  'blues-major-pentatonic': ['1', '2', '4', '5', '6'],
  'whole-tone': ['1', '2', '3', '#4', '#5', '#6'],
  augmented: ['1', '#2', '3', '5', 'b6', '7'],
  'inverted-augmented': ['1', 'b2', '3', '4', '#5', '6'],
  prometheus: ['1', '2', '3', '#4', '6', 'b7'],
  tritone: ['1', 'b2', '3', '#4', '5', 'b7'],
  hexatonic: ['1', 'b3', '4', 'b5', '5', 'b7'],
  'major-blues': ['1', '2', 'b3', '3', '5', '6'],
  'nonatonic-blues': ['1', '2', 'b3', '3', '4', '5', '6', 'b7', '7'],
  'decatonic-blues': ['1', '2', 'b3', '3', '4', 'b5', '5', '6', 'b7', '7'],
  'bebop-dominant': ['1', '2', '3', '4', '5', '6', 'b7', '7'],
  'bebop-dorian': ['1', '2', 'b3', '3', '4', '5', '6', 'b7'],
  'alternate-bebop-dorian': ['1', '2', 'b3', '4', '5', '6', 'b7', '7'],
  'bebop-major': ['1', '2', '3', '4', '5', '#5', '6', '7'],
  'bebop-melodic-minor': ['1', '2', 'b3', '4', '5', '#5', '6', '7'],
  'bebop-harmonic-minor': ['1', '2', 'b3', '4', '5', 'b6', 'b7', '7'],
  'double-harmonic-major': ['1', 'b2', '3', '4', '5', 'b6', '7'],
  'lydian-2-6': ['1', '#2', '3', '#4', '5', '#6', '7'],
  ultraphrygian: ['1', 'b2', 'b3', 'b4', '5', 'b6', 'bb7'],
  'hungarian-minor': ['1', '2', 'b3', '#4', '5', 'b6', '7'],
  oriental: ['1', 'b2', '3', '4', 'b5', '6', 'b7'],
  'ionian-2-5': ['1', '#2', '3', '4', '#5', '6', '7'],
  'locrian-bb3-bb7': ['1', 'b2', 'bb3', '4', 'b5', 'b6', 'bb7'],
  'neapolitan-major': ['1', 'b2', 'b3', '4', '5', '6', '7'],
  'leading-whole-tone': ['1', '2', '3', '#4', '#5', '#6', '7'],
  'lydian-dominant-augmented': ['1', '2', '3', '#4', '#5', '6', 'b7'],
  'lydian-minor': ['1', '2', '3', '#4', '5', 'b6', 'b7'],
  'major-locrian': ['1', '2', '3', '4', 'b5', 'b6', 'b7'],
  'altered-dominant-major-2nd': ['1', '2', 'b3', 'b4', 'b5', 'b6', 'b7'],
  'altered-dominant-diminished-3rd': ['1', 'b2', 'bb3', 'b4', 'b5', 'b6', 'b7'],
  'neapolitan-minor': ['1', 'b2', 'b3', '4', '5', 'b6', '7'],
  'lydian-6': ['1', '2', '3', '#4', '5', '#6', '7'],
  'mixolydian-augmented': ['1', '2', '3', '4', '#5', '6', 'b7'],
  'hungarian-gypsy': ['1', '2', 'b3', '#4', '5', 'b6', 'b7'],
  'locrian-dominant': ['1', 'b2', '3', '4', 'b5', 'b6', 'b7'],
  'ionian-2': ['1', '#2', '3', '4', '5', '6', '7'],
  'ultralocrian-bb3': ['1', 'b2', 'bb3', 'b4', 'b5', 'b6', 'bb7'],
  'hungarian-major': ['1', '#2', '3', '#4', '5', '6', 'b7'],
  'ultralocrian-bb6': ['1', 'b2', 'b3', 'b4', 'b5', 'bb6', 'bb7'],
  'harmonic-minor-b5': ['1', '2', 'b3', '4', 'b5', 'b6', '7'],
  'superlocrian-nat6': ['1', 'b2', 'b3', 'b4', 'b5', '6', 'b7'],
  'jazz-minor-5': ['1', '2', 'b3', '4', '#5', '6', '7'],
  'dorian-b2-4': ['1', 'b2', 'b3', '#4', '5', '6', 'b7'],
  'nohkan-flute-scale': ['1', '2', '#3', '#4', '#5', '6', '7'],
  'romanian-major': ['1', 'b2', '3', '#4', '5', '6', 'b7'],
  'super-lydian-augmentednat6': ['1', '#2', '#3', '#4', '#5', '6', '7'],
  'locriannat2-bb7': ['1', '2', 'b3', '4', 'b5', 'b6', 'bb7'],
  'superlocrian-bb6': ['1', 'b2', 'b3', 'b4', 'b5', 'bb6', 'b7'],
  'jazz-minor-b5': ['1', '2', 'b3', '4', 'b5', '6', '7'],
  'javanese-b4': ['1', 'b2', 'b3', 'b4', '5', '6', 'b7'],
  'lydian-augmented-b3': ['1', '2', 'b3', '#4', '#5', '6', '7'],
  chromatic: ['1', 'b2', '2', 'b3', '3', '4', '#4', '5', 'b6', '6', 'b7', '7'],
};

/** Semitones above the tonic of each major-scale step (step 1 = the tonic). */
export const MAJOR_STEP_SEMITONES: readonly number[] = [0, 2, 4, 5, 7, 9, 11];

export const SEMITONES_PER_OCTAVE = 12;

const DEGREE_PATTERN = /^(b{0,2}|#{0,2})([1-7])$/;

export interface ParsedDegree {
  /** The scale step, 1-7: fixes the note's letter. */
  step: number;
  /** Semitones off the major scale's step: -2 (bb) to +2 (##). */
  alteration: number;
}

/** Parse a degree ('1', 'b3', '#4', 'bb7'); throws on anything else. */
export const parseDegree = (degree: ScaleDegree): ParsedDegree => {
  const match = DEGREE_PATTERN.exec(degree);
  if (!match) {
    throw new Error(`scale formula: "${degree}" is not a scale degree`);
  }
  const [, accidentals, step] = match;
  const alteration = accidentals.startsWith('#') ? accidentals.length : -accidentals.length;
  return { step: Number(step), alteration };
};

/** Semitones above the tonic of one degree. */
export const degreeSemitones = (degree: ScaleDegree): number => {
  const { step, alteration } = parseDegree(degree);
  return MAJOR_STEP_SEMITONES[step - 1] + alteration;
};

/** The unit's formula; throws on an unknown unit id. */
export const scaleFormula = (unitId: string): readonly ScaleDegree[] => {
  const formula = SCALE_FORMULAS[unitId];
  if (!formula) {
    throw new Error(`scale formula: no formula for scale unit "${unitId}"`);
  }
  return formula;
};

/**
 * The unit's step pattern in semitones, one step per degree, the last step
 * returning to the tonic an octave up (so the steps add up to 12). This is the
 * `intervals` a stepwise run walks: major is [2, 2, 1, 2, 2, 2, 1].
 */
export const scaleStepIntervals = (unitId: string): number[] => {
  const offsets = scaleFormula(unitId).map(degreeSemitones);
  return offsets.map((offset, index) =>
    index === offsets.length - 1 ? SEMITONES_PER_OCTAVE - offset : offsets[index + 1] - offset,
  );
};
